import { Injectable, OnDestroy } from '@angular/core';
import { DynamicTableColDef, PagedRecords } from '@frk/eds-components';
import documentListingQuery from '@graphql/fund-document/fund-documents-all-products.graphql';
import { ADVISOR, Z_SHARECLASS } from '@literature/const/literature-data-const';
import { fundNameGenerator, nameGenerator } from '@literature/literature-utils';
import { CartHandlerService } from '@literature/services/cart-handler.service';
import { LiteratureDataService } from '@literature/services/literature-data.service';
import { LiteratureFundDataService } from '@literature/services/literature-fund-data.service';
import { LiteratureGlobalConfigService } from '@literature/services/literature-global-config.service';
import { Product, ShareClass, Identifiers, IdentifierKey } from '@models';
import { FundFilterService } from '@products/fund-listing/services/fund-filter.service';
import { ProductTypeParameter } from '@products/services/graphql-fund-data.service';
import {
  DebugService,
  IUserProfile,
  PersonalisationAPIService,
  ProfileService,
  SegmentService,
} from '@services';
import { TranslateService } from '@shared/translate/translate.service';
import {
  ConfigurationId,
  CustomLiteratureDocumentListingData,
  DctermType,
  DocumentMatchCondition,
  FundId,
  ISIN,
  KeyIdentifiers,
  LiteratureData,
  LiteratureDocument,
  LiteratureDocumentListingData,
  LiteratureListingConfiguration,
  LiteraturePageType,
  ShareClassCode,
  ShareclassLevelLiteratureDocumentListingData,
} from '@types';
import { Logger } from '@utils/logger';
import { literatureSort } from '@utils/sort-utils';
import cloneDeep from 'lodash/cloneDeep';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { retry, switchMap, takeUntil } from 'rxjs/operators';
import { LiteratureListingBaseService } from './literature-listing-base.service';
import { EMDASH, NA } from '@products/utils/constants/product.constants';
import { COLDEF } from 'src/app/ft-components/interactive-content/ucits-etf-data-table/types/model/indexes';
import {
  ExcelDownloadService,
  FtCellObject,
  getCellObject,
  getColWidths,
} from '@products/services/excel-download.service';
import {
  GlobalRegulatoryShareClassDetails,
  GlobalRegulatoryShareclassInfo,
} from '@literature/types/global-regulatory.type';

const logger = Logger.getLogger('LiteratureDocumentListingService');

@Injectable({
  providedIn: 'root',
})
export class LiteratureDocumentListingService
  extends LiteratureListingBaseService
  implements OnDestroy {
  private listingData: LiteratureDocumentListingData[];
  private customListingData: CustomLiteratureDocumentListingData[] = [];
  private shareclassLevelListingData: ShareclassLevelLiteratureDocumentListingData[] = [];
  public dateFormat: string;
  public dataInitialised = false;
  private dataState: string; // TODO: can this be deleted now?
  public pagedData: PagedRecords<LiteratureDocumentListingData> = new PagedRecords<LiteratureDocumentListingData>(
    100
  ); // TODO: use const/config for pageSize
  private totalNumberOfData = new Subject();
  private literaturePageConfig: LiteratureListingConfiguration;
  private mappedFullRecords: FtCellObject[][];

  constructor(
    protected literatureDataService: LiteratureDataService,
    private fundDataService: LiteratureFundDataService,
    protected segmentService: SegmentService,
    private filterService: FundFilterService,
    protected translateService: TranslateService,
    protected cartService: CartHandlerService,
    protected profileService: ProfileService,
    protected personalisationService: PersonalisationAPIService,
    protected literatureConfigService: LiteratureGlobalConfigService,
    protected debugService: DebugService,
    private excelDownloadService: ExcelDownloadService
  ) {
    super(
      literatureDataService,
      literatureConfigService,
      cartService,
      segmentService,
      profileService,
      personalisationService,
      translateService
    );
    this.filterService.getData().subscribe(([dataType, data]) => {
      if ('filteredList' === dataType || 'requery' === dataType) {
        this.dataState = dataType;
        this.dataInitialised = true;
        this.state.data = data;
        this.documentListingData$.next(this.state);
        this.pagedData.records = data;
      }
    });
    this.debugService
      .getExpandAllComponents$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((expandAll: boolean): void => {
        if (expandAll) {
          this.pagedData.expandAll();
        }
      });
  }

  private dataMap: Map<FundId, Product> = new Map();
  private fundUmbrellaMap: Map<string, [any]> = new Map<string, [any]>();
  private fundLevelLitDataMap: Map<FundId, LiteratureDocument[]> = new Map();

  // both the object's key and size is dynamic and based on number of shareclasses combination document has.
  private singleShareClassData: object = {};
  private multiShareClassData: object = {};

  public populate(
    param: Map<string, string>,
    pageType: LiteraturePageType,
    isShareclassLevelRegulatory = false
  ): void {
    this.literaturePageConfig = this.literatureConfigService.getLiteratureListingConfig();
    if (this.isInternational) {
      forkJoin([
        this.fundDataService.register(
          documentListingQuery,
          {
            configurationName: ConfigurationId.MF,
            productType: ProductTypeParameter.MUTUAL_FUNDS,
          },
          this.literatureConfigService.literatureConfig?.activeList,
          this.literatureConfigService.literatureConfig?.fundOverrideFlag
        ),
        this.literatureDataService.loadData(param),
      ]).subscribe(
        (response) => {
          switch (pageType) {
            case LiteraturePageType.REGULATORY_FUND_DOCUMENTS:
              this.customMerge(response, isShareclassLevelRegulatory);
              break;
            case LiteraturePageType.REGULATORY_FUND_DOCUMENTS_GLOBAL:
              this.shareclassLayoutMerge(response);
              break;
            default:
              this.mergeData(response);
          }
        },
        (err) => {
          logger.error(
            '[populateLiteratureData] error while getting data from backend service : ' +
              JSON.stringify(err) +
              ' [error status]: ' +
              err.status
          );
          this.state.isError = true;
          this.state.data = [];
          this.documentListingData$.next(this.state);
        }
      );
    } else {
      this.literatureDataService
        .getProfileData$()
        ?.pipe(
          takeUntil(this.unsubscribe$),
          switchMap((profileData: IUserProfile) => {
            this.literatureDataService.setProfileData(profileData);
            return forkJoin([
              this.fundDataService.register(
                documentListingQuery,
                {
                  configurationName: ConfigurationId.MF,
                  productType: ProductTypeParameter.MUTUAL_FUNDS,
                },
                this.literatureConfigService.literatureConfig?.activeList,
                this.literatureConfigService.literatureConfig?.fundOverrideFlag
              ),
              this.literatureDataService.loadData(param, true),
            ]);
          }),
          retry(1)
        )
        .subscribe(
          (response) => {
            switch (pageType) {
              case LiteraturePageType.REGULATORY_FUND_DOCUMENTS:
                this.customMerge(response, isShareclassLevelRegulatory);
                break;
              case LiteraturePageType.REGULATORY_FUND_DOCUMENTS_GLOBAL:
                this.shareclassLayoutMerge(response);
                break;
              default:
                this.mergeData(response);
            }
          },
          (err) => {
            logger.error(
              '[populateLiteratureData] error while getting data from backend service : ' +
                JSON.stringify(err) +
                ' [error status]: ' +
                err.status
            );
            this.state.isError = true;
            this.state.data = [];
            this.documentListingData$.next(this.state);
          }
        );
    }
  }

  public getColumnToDisplay(): string[] {
    return this.literaturePageConfig?.literatureListGridColumn;
  }

  private getLoggedInColumnToDisplay(): string[] {
    return this.literaturePageConfig?.documentTypeDisplayForLoggedIn;
  }

  public getDocumentToDisplay(): string[] {
    return this.literaturePageConfig?.documentTypeDisplay;
  }

  /**
   * Prepares data for literature listing page from PPSS data and literature data
   * @param gridData LiteratureDocumentListingData
   * @returns LiteratureDocumentListingData
   */
  private mergeData(gridData): LiteratureDocumentListingData[] {
    this.loggedInColumnToDisplay = this.getLoggedInColumnToDisplay();
    logger.debug('merge data: ', gridData);
    this.listingData = [];
    const [fundListing, litData] = gridData;
    this.dateFormat = this.literatureConfigService.dateFormat;

    this.productDataMergeInitializer(fundListing._products);
    const colToDisplay = this.getDocumentToDisplay();
    const filters = [
      ...this.literaturePageConfig?.primaryFilters,
      ...this.literaturePageConfig?.additionalFilters,
    ];
    for (const literatureData of litData.document) {
      let litSet: Set<string>;
      const data = this.literatureDataService.literatureMapper(literatureData);

      // WDE-441 flag usePreviewLink informs components to redirect user to preview page instead download.
      data.usePreviewLink = this.literatureConfigService?.literatureConfig?.documentsWithPreview?.includes(
        data.dctermsType
      );

      if (
        !this.checkAudienceAndDocumentTypeConfiguration(
          data,
          filters,
          colToDisplay
        )
      ) {
        continue;
      }
      const frkOneTISNumber = literatureData.frkOneTISNumber
        ? literatureData.frkOneTISNumber.split(',')
        : [];
      if (!this.isInternational) {
        // when there is no association with funds need to show that documents also
        if (frkOneTISNumber.length === 0 && !data.frkFundGroupCode) {
          this.loadDocumentList(data, {}, this.listingData);
        }
        litSet = new Set<string>();
      }
      let matchFound = false;
      const matchOn = this.literatureConfigService
        .getDocumentMappingConfiguration()
        .get(data.dctermsType);
      switch (matchOn) {
        case DocumentMatchCondition.UNASSOCIATED:
          data.identifiers = this.initializeIdentifier();
          this.loadDocumentList(data, {}, this.listingData, true);
          break;
        // tslint:disable-next-line: no-switch-case-fall-through
        case DocumentMatchCondition.FUND_ID:
          matchFound = this.matchOnFundId(frkOneTISNumber, data, litSet);
          if (matchFound) {
            break;
          }
        // tslint:disable-next-line: no-switch-case-fall-through
        case DocumentMatchCondition.FUND_UMBRELLA:
          matchFound = this.matchOnFundUmbrella(data);
          if (matchFound) {
            break;
          }
        // tslint:disable-next-line: no-switch-case-fall-through
        default:
          if (!this.matchOnFundId(frkOneTISNumber, data, litSet)) {
            this.matchOnFundUmbrella(data);
          }
      }
    }

    this.state.data = this.listingData;
    this.filterService.init(this.listingData, 'Literature');
    this.totalNumberOfData.next(this.listingData.length);
    return this.listingData;
  }

  private matchOnFundId(
    frkOneTISNumber: string[],
    data: LiteratureDocument,
    litSet: Set<string>
  ): boolean {
    data.multipleProductsForSearch = {
      fundNames: [],
    };
    // lit api records based on onetis number which can be multiple with one document
    if (frkOneTISNumber.length > 0) {
      for (const element of frkOneTISNumber) {
        // if site is US then incase of multiple onetis number only add document for oneTis number
        // To avoid duplicate records

        if (!this.isInternational) {
          if (litSet.has(data.docId)) {
            break;
          }
          // to store multiple products data associated to single literature document for 'SEARCH BY KEYWORD' filter
          data.multipleProductsForSearch.fundIds = cloneDeep(frkOneTISNumber);
          frkOneTISNumber.forEach((el) => {
            if (this.dataMap.get(el?.trim() as FundId)?.shareClasses) {
              data.multipleProductsForSearch.fundNames.push(
                this.dataMap.get(el?.trim() as FundId)?.fundName
              );
              data.multipleProductsForSearch.identifiers = this.getKeyIdentifiers(
                this.dataMap.get(el?.trim() as FundId)?.shareClasses,
                data.multipleProductsForSearch.identifiers
                  ? data.multipleProductsForSearch.identifiers
                  : this.initializeIdentifier()
              );
            }
          });
        }
        const productData: Product = this.dataMap.get(
          element?.trim() as FundId
        );
        if (
          this.dataMap.has(element?.trim() as FundId) &&
          productData.shareClasses?.length > 0
        ) {
          // litSet is only specific to US
          litSet?.add(data.docId);
          this.matchOnShareClass(data, productData);
        }
      }
      return true;
    }
    return false;
  }

  private matchOnFundUmbrella(data: LiteratureDocument): boolean {
    if (data.frkFundGroupCode) {
      const fundUmbrella = this.fundUmbrellaMap.get(
        data.frkFundGroupCode.trim()
      );
      if (fundUmbrella) {
        fundUmbrella.forEach((productData) => {
          if (productData.shareClasses?.length > 0) {
            this.matchOnShareClass(data, productData);
          }
        });
        return true;
      }
    }
    return false;
  }

  private matchOnShareClass(data: LiteratureDocument, productData: any): void {
    const matchOn = this.literatureConfigService
      .getDocumentMappingConfiguration()
      .get(data.dctermsType);
    if (
      (!matchOn && data.frkShareClassCode?.length > 1) ||
      (DocumentMatchCondition.FUND_SHARE_CLASS === matchOn &&
        data.frkShareClassCode?.length > 1)
    ) {
      for (const shareClassCode of data.frkShareClassCode) {
        const clonedLitData = cloneDeep(data);
        clonedLitData.frkShareClassCode = [shareClassCode];
        this.loadShareClassData(
          clonedLitData,
          productData,
          this.listingData,
          !(matchOn === DocumentMatchCondition.FUND_ID)
        );
      }
    } else {
      this.loadShareClassData(
        cloneDeep(data),
        productData,
        this.listingData,
        !(matchOn === DocumentMatchCondition.FUND_ID)
      );
    }
  }

  private productDataMergeInitializer(ppssListing): void {
    for (const productData of ppssListing) {
      // to map product data at fund ID level
      // WDE-5738 - EFT fund id 2545 is duplicate so we need to check weather fund id already exist in dataMap or not
      if (
        this.dataMap.has(productData.fundId) &&
        this.literatureConfigService.isCanada
      ) {
        const existingProductData = this.dataMap.get(productData.fundId);
        if (productData.shareClasses.length) {
          existingProductData.shareClasses.push(...productData.shareClasses);
        }
        this.dataMap.set(existingProductData.fundId, existingProductData);
      } else {
        this.dataMap.set(productData.fundId, productData);
      }
      const fundUmbrella = productData.fundUmbrella;
      // to map product data at fundUmberella level
      if (fundUmbrella) {
        if (this.fundUmbrellaMap.has(fundUmbrella)) {
          const ppssArray = this.fundUmbrellaMap.get(fundUmbrella);
          ppssArray.push(productData);
          this.fundUmbrellaMap.set(fundUmbrella, ppssArray);
        } else {
          this.fundUmbrellaMap.set(fundUmbrella, [productData]);
        }
      }
    }
  }

  private shareclassLayoutMerge(
    data
  ): ShareclassLevelLiteratureDocumentListingData[] {
    logger.debug('shareclass level merge data: ', data);
    this.listingData = [];
    const [fundListing, litData] = data;
    this.dateFormat = this.literatureConfigService.dateFormat;
    const litDataMap: Map<
      FundId,
      Map<ShareClassCode, LiteratureDocument[]>
    > = this.getFundAndShareclassLevelData(litData);
    this.productDataMergeInitializer(fundListing._products);
    litDataMap.forEach((value, key) => {
      const productData: Product = this.dataMap.get(key);
      if (this.dataMap.has(key) && productData.shareClasses?.length > 0) {
        this.grouByDctermtypeData(litDataMap.get(key), productData);
      }
    });
    // if all the literature document of a product are at fund level
    this.fundLevelLitDataMap.forEach((value, key) => {
      if (!litDataMap.has(key)) {
        const productData: Product = this.dataMap.get(key);
        if (this.dataMap.has(key) && productData.shareClasses?.length > 0) {
          const FundSpecificDoc: Map<
            ShareClassCode,
            LiteratureDocument[]
          > = new Map();
          FundSpecificDoc.set(
            EMDASH as ShareClassCode,
            this.fundLevelLitDataMap.get(key)
          );
          this.grouByDctermtypeData(FundSpecificDoc, productData);
        }
      }
    });
    this.state.data = this.shareclassLevelListingData;
    this.filterService.init(this.state.data, 'Literature');
    this.totalNumberOfData.next(this.shareclassLevelListingData.length);
    return this.shareclassLevelListingData;
  }

  private getFundAndShareclassLevelData(
    litData: LiteratureData
  ): Map<FundId, Map<ShareClassCode, LiteratureDocument[]>> {
    const columnToDisplay = this.getDocumentToDisplay();
    const litDataMap: Map<
      FundId,
      Map<ShareClassCode, LiteratureDocument[]>
    > = new Map();

    for (const literatureData of litData.document) {
      const frkOneTISNumber = literatureData.frkOneTISNumber
        ? literatureData.frkOneTISNumber
            .split(',')
            .map((oneTisNumber) => oneTisNumber.trim())
        : [];
      const mappedData = this.literatureDataService.literatureMapper(
        literatureData
      );

      this.literatureDataService.mapLiteratureUrl(mappedData);
      this.literatureDataService.mapPrimaryLanguage(mappedData);
      // to map all the data with same onetis Number
      if (frkOneTISNumber.length > 0) {
        frkOneTISNumber.forEach((fundId) => {
          if (
            columnToDisplay.indexOf(mappedData.dctermsType) !== -1 &&
            this.isDocumentDownloadValid(mappedData) // Added this logic to remove documents which are not valid as per fund header logic
          ) {
            if (litDataMap.has(fundId as FundId)) {
              const mapData: Map<
                ShareClassCode,
                LiteratureDocument[]
              > = litDataMap.get(fundId as FundId);
              if (mappedData?.frkShareClassCode?.length > 0) {
                mappedData.frkShareClassCode.forEach((shareClassCode) => {
                  if (mapData.has(shareClassCode)) {
                    const tempData = mapData.get(shareClassCode);
                    tempData.push(mappedData);
                    mapData.set(shareClassCode, tempData);
                  } else {
                    mapData.set(shareClassCode as ShareClassCode, [mappedData]);
                  }
                });
                litDataMap.set(fundId as FundId, mapData);
              } else {
                this.setFundLevelLiteratureData(fundId as FundId, mappedData);
              }
            } else {
              const litDataMapWithFrkShareclassCode: Map<
                ShareClassCode,
                LiteratureDocument[]
              > = new Map();
              if (mappedData?.frkShareClassCode?.length > 0) {
                mappedData.frkShareClassCode.forEach((shareClassCode) => {
                  litDataMapWithFrkShareclassCode.set(
                    shareClassCode as ShareClassCode,
                    [mappedData]
                  );
                });
                litDataMap.set(
                  fundId as FundId,
                  litDataMapWithFrkShareclassCode
                );
              } else {
                this.setFundLevelLiteratureData(fundId as FundId, mappedData);
              }
            }
          }
        });
      }
    }

    return litDataMap;
  }

  private setFundLevelLiteratureData(
    fundId: FundId,
    mappedData: LiteratureDocument
  ): void {
    if (this.fundLevelLitDataMap.has(fundId)) {
      const fundLevelData = this.fundLevelLitDataMap.get(fundId);
      fundLevelData.push(mappedData);
      this.fundLevelLitDataMap.set(fundId, fundLevelData);
    } else {
      this.fundLevelLitDataMap.set(fundId, [mappedData]);
    }
  }

  public regulatoryExcelDownload(): void {
    forkJoin([
      this.excelDownloadService.getProximalCaveat$(
        'RegulatoryFundDocumentBottom'
      ),
      this.excelDownloadService.getProximalCaveat$('RegulatoryFundDocumentTop'),
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data) => {
        const [bottomCaveat, topCaveat]: [
          FtCellObject[][],
          FtCellObject[][]
        ] = data;
        this.exportToExcel(topCaveat, bottomCaveat);
      });
  }

  private exportToExcel(
    topCaveat: FtCellObject[][],
    bottomCaveat: FtCellObject[][]
  ): void {
    import('xlsx').then((xlsx) => {
      // Create new worksheet with topCaveat
      const workSheet = xlsx.utils.aoa_to_sheet(topCaveat);
      const workBook = xlsx.utils.book_new();

      // Feet to columns
      workSheet['!cols'] = getColWidths(this.mappedFullRecords);

      // Append data
      xlsx.utils.sheet_add_aoa(workSheet, this.mappedFullRecords, {
        origin: 'A' + (topCaveat.length + 2),
      });

      // Add bottom caveat
      xlsx.utils.sheet_add_aoa(workSheet, bottomCaveat, {
        origin:
          'A' + (topCaveat.length + 2 + this.mappedFullRecords.length + 2),
      });

      xlsx.utils.book_append_sheet(
        workBook,
        workSheet,
        this.translateService.instant('literature.regulatory-report-tabname')
      );
      xlsx.writeFile(
        workBook,
        this.translateService.instant('literature.regulatory-report-filename')
      );
    });
  }

  public prepareShareClassLevelExcelData(
    data: ShareclassLevelLiteratureDocumentListingData[],
    colDef: DynamicTableColDef<COLDEF>[]
  ): Observable<boolean> {
    let isExcelDataFormated = false;
    const mappedBody: { [key: string]: string }[] = this.mapDownloadData(
      data,
      colDef
    );
    this.mappedFullRecords = [
      colDef.map((col) => getCellObject(col.headerName)),
    ];

    mappedBody.forEach((mappedElement) => {
      this.mappedFullRecords.push(
        (Object.values(mappedElement) as unknown) as FtCellObject[]
      );
    });
    isExcelDataFormated = true;
    return of(isExcelDataFormated);
  }

  private mapDownloadData(
    data: ShareclassLevelLiteratureDocumentListingData[],
    colDef: DynamicTableColDef<COLDEF>[]
  ) {
    const mappedBody: { [key: string]: string }[] = [];
    data.forEach((globalRegulatoryRowData) => {
      Object.keys(globalRegulatoryRowData.literatureData).forEach(
        (shareclassId) => {
          // Object keys are dynamic and set based on Doc type selected form BR
          const excelRowObject: { [key: string]: string } = {};
          const shareclassOptionData = globalRegulatoryRowData.shareclassOption.filter(
            (option) => option.id === shareclassId
          );
          if (shareclassOptionData.length) {
            excelRowObject.fundname = globalRegulatoryRowData.product.fundName;
            excelRowObject.shareClass = shareclassOptionData[0]?.link;
            for (let i = 2; i < colDef.length - 1; i++) {
              excelRowObject[colDef[i]?.field] = globalRegulatoryRowData
                .literatureData[shareclassId][colDef[i]?.field]
                ? this.extractDomain() +
                  globalRegulatoryRowData.literatureData[shareclassId][
                    colDef[i]?.field
                  ]?.pdfDownloadLink
                : NA;
            }
            excelRowObject.additionalDocuments =
              this.extractDomain() + shareclassOptionData[0]?.productDetailLink;

            mappedBody.push(excelRowObject);
          }
        }
      );
    });
    return mappedBody;
  }

  private customMerge(
    data,
    isShareclassLevelRegulatory = false
  ): CustomLiteratureDocumentListingData[] {
    this.loggedInColumnToDisplay = this.getLoggedInColumnToDisplay();
    logger.debug('custom merge data: ', data);
    this.listingData = [];
    const [fundListing, litData] = data;
    this.dateFormat = this.literatureConfigService.dateFormat;
    const litDataMap: Map<
      FundId,
      Map<DctermType, LiteratureDocument[]>
    > = new Map();
    const columnToDisplay = this.getDocumentToDisplay();

    this.productDataMergeInitializer(fundListing._products);

    for (const literatureData of litData.document) {
      const frkOneTISNumber = literatureData.frkOneTISNumber
        ? literatureData.frkOneTISNumber
            .split(',')
            .map((oneTisNumber) => oneTisNumber.trim())
        : [];
      data = this.literatureDataService.literatureMapper(literatureData);
      this.literatureDataService.mapLiteratureUrl(data);
      // to map all the data with same onetis Number
      if (frkOneTISNumber.length > 0) {
        frkOneTISNumber.forEach((element) => {
          if (columnToDisplay.indexOf(data.dctermsType) !== -1) {
            if (litDataMap.has(element)) {
              const mapData: Map<
                DctermType,
                LiteratureDocument[]
              > = litDataMap.get(element);
              if (mapData.has(data.dctermsType)) {
                const tempData = mapData.get(data.dctermsType);
                tempData.push(data);
                mapData.set(data.dctermsType, tempData);
              } else {
                mapData.set(data.dctermsType as DctermType, [data]);
              }
              litDataMap.set(element, mapData);
            } else {
              const litDataMapWithDctermType: Map<
                DctermType,
                LiteratureDocument[]
              > = new Map();
              litDataMapWithDctermType.set(data.dctermsType as DctermType, [
                data,
              ]);
              litDataMap.set(element, litDataMapWithDctermType);
            }
          }
        });
      }
    }
    litDataMap.forEach((value, key) => {
      const productData: Product = this.dataMap.get(key);
      if (this.dataMap.has(key) && productData.shareClasses?.length > 0) {
        this.customLoadShareClassData(
          litDataMap.get(key),
          productData,
          isShareclassLevelRegulatory
        );
      }
    });
    this.state.data = this.customListingData;
    this.filterService.init(this.state.data, 'Literature');
    this.totalNumberOfData.next(this.customListingData.length);
    return this.customListingData;
  }

  public sortDataByTicker(
    data:
      | LiteratureDocumentListingData[]
      | CustomLiteratureDocumentListingData[],
    pageType: LiteraturePageType
  ): LiteratureDocumentListingData[] | CustomLiteratureDocumentListingData[] {
    const nameCompare = literatureSort;
    let sortedData:
      | LiteratureDocumentListingData[]
      | CustomLiteratureDocumentListingData[] = [];
    if (pageType === LiteraturePageType.REGULATORY_FUND_DOCUMENTS) {
      sortedData = data.sort((a, b) => {
        return nameCompare(a.product.fundName, b.product.fundName, a, b, false);
      });
    } else {
      sortedData = data.sort((a, b) => {
        return nameCompare(
          this.isInternational
            ? nameGenerator(
                a,
                this.translateService,
                this.literatureConfigService.isCanada
              )
            : fundNameGenerator(a, this.translateService),
          this.isInternational
            ? nameGenerator(
                b,
                this.translateService,
                this.literatureConfigService.isCanada
              )
            : fundNameGenerator(b, this.translateService),
          a,
          b,
          false
        );
      });
    }

    this.pagedData.records = sortedData;
    return sortedData;
  }

  // TODO: need return type
  public getTotalNumberofData() {
    return this.totalNumberOfData;
  }

  private checkAudienceAndDocumentTypeConfiguration(
    data: LiteratureDocument,
    filters: string[],
    colToDisplay: string[]
  ): boolean {
    const isLanguageFilterConfigured = filters.indexOf('LANGUAGE') > -1;
    const languageFilterArray: string[] = this.literaturePageConfig
      ?.languageFilterArray;
    let LanguageMatch = false;
    if (isLanguageFilterConfigured) {
      if (languageFilterArray?.length > 0) {
        for (const language of languageFilterArray) {
          if (data.dctermsLanguage?.indexOf(language) !== -1) {
            LanguageMatch = true;
            data.languageFilterValues.push(language);
            break;
          }
        }
      } else {
        data.languageFilterValues = [...data.dctermsLanguage];
      }
    }
    // Audience new flow logic to check map
    return (
      colToDisplay?.indexOf(data.dctermsType) !== -1 &&
      (isLanguageFilterConfigured && languageFilterArray?.length > 0
        ? LanguageMatch
        : true)
    );
  }

  public updateNumberOfRows(numberOfRows: number): void {
    this.pagedData.pageSize = numberOfRows;
  }

  public getPageSize(): number {
    return this.pagedData.pageSize;
  }

  public goToPage(n: number): void {
    if (this.pagedData) {
      this.pagedData.pageNumber = n;
    }
  }

  public getNumberOfPages$(): Observable<number> {
    return this.pagedData.numberOfPages$;
  }

  // TODO: can this be deleted now?
  private getNumberOfPages(): number {
    return this.pagedData.numberOfPages;
  }

  public getPageNumber$(): Observable<number> {
    return this.pagedData.pageNumber$;
  }

  public getCurrentPage$(): Observable<LiteratureDocumentListingData[]> {
    return this.pagedData.currentPage$;
  }

  private loadShareClassData(
    litData: LiteratureDocument,
    ppssData: any,
    listingData: LiteratureDocumentListingData[],
    showShareClass: boolean
  ): void {
    this.checkShareClassAvailablity(litData, ppssData, showShareClass);
    if (litData.allShareClass.length > 0) {
      litData.identifiers = this.getKeyIdentifiers(
        litData.allShareClass,
        this.initializeIdentifier()
      );
    } else {
      litData.identifiers = this.getKeyIdentifiers(
        ppssData.shareClasses,
        this.initializeIdentifier()
      );
    }
    this.loadDocumentList(litData, ppssData, listingData);
  }

  // to create multiple row for the fund having same document type
  private customLoadShareClassData(
    litData: Map<DctermType, LiteratureDocument[]>,
    ppssData: Product,
    isShareclassLevelRegulatory = false
  ): void {
    this.singleShareClassData = {};
    this.multiShareClassData = {};
    // segregate the data at multishareclass level & singleshareclass level
    this.setDataOnShareClassLevel(litData, ppssData);

    // to create multiple singleShareClass Data when single share class
    // is coming up with multiple combination in multiShareClassData
    if (Object.keys(this.multiShareClassData).length > 1) {
      this.splitMultiShareClassDataToSingleShareClassData();
    }

    // generate rows for listing
    if (
      Object.keys(this.singleShareClassData).length === 0 &&
      Object.keys(this.multiShareClassData).length > 0
    ) {
      // directly pass 'multiShareClassData' data for generating rows
      this.generateCustomRow(
        this.multiShareClassData,
        ppssData,
        isShareclassLevelRegulatory
      );
    } else if (
      Object.keys(this.multiShareClassData).length === 0 &&
      Object.keys(this.singleShareClassData).length > 0
    ) {
      // directly pass 'singleShareClassData' data for generating rows
      this.generateCustomRow(
        this.singleShareClassData,
        ppssData,
        isShareclassLevelRegulatory
      );
    } else {
      this.mergeMultiShareClassDataToSingleShareClassData(
        ppssData,
        isShareclassLevelRegulatory
      );
    }
  }

  private grouByDctermtypeData(
    litData: Map<ShareClassCode, LiteratureDocument[]>,
    ppssData: Product
  ): void {
    const fundSpecificLiteratureData = {};

    litData.forEach((value: LiteratureDocument[], key: ShareClassCode) => {
      const shareclassSpecificLiteratureData = {};
      value
        .sort(this.sortDocuments(this.getDocumentToDisplay()))

        .filter(
          (litDoc, litIndex, litDocArray) =>
            litDocArray.findIndex(
              (t: LiteratureDocument) => t.dctermsType === litDoc.dctermsType
            ) === litIndex
        ) // filters only one document of specific type

        .forEach((document: LiteratureDocument) => {
          if (!shareclassSpecificLiteratureData[document?.dctermsType]) {
            shareclassSpecificLiteratureData[document?.dctermsType] = document;
          }
        });
      fundSpecificLiteratureData[key] = shareclassSpecificLiteratureData;
    });
    this.mergeFundLevelDocuments(fundSpecificLiteratureData, ppssData?.fundId);
    this.shareclassLevelListingData.push({
      literatureData: fundSpecificLiteratureData,
      product: ppssData,
      primaryShareclassCode: this.getPrimaryShareClass(ppssData),
      ...this.generateRegulatoryShareClassDetails(
        fundSpecificLiteratureData,
        ppssData
      ),
    });
  }

  /**
   * sorts by config order and then by publicationDate desc, primary Language.
   * @param literature header config
   */
  private sortDocuments = (config) => (
    docA: LiteratureDocument,
    docB: LiteratureDocument
  ) => {
    const indexA = config.indexOf(docA.dctermsType);
    const indexB = config.indexOf(docB.dctermsType);
    const pubDateA = docA.publicationDate;
    const pubDateB = docB.publicationDate;
    const langA = docA.hasPrimaryLanguage ? 1 : 0;
    const langB = docB.hasPrimaryLanguage ? 1 : 0;

    return (
      indexA - indexB || pubDateB?.localeCompare(pubDateA) || langB - langA
    );
  };

  /**
   * verifies if link is valid - this method may be extended in the future
   */
  private isDocumentDownloadValid(litDocument: LiteratureDocument): boolean {
    return (
      litDocument?.pdfDownloadLink?.length > 1 &&
      litDocument?.docId &&
      litDocument.downloadUrl &&
      this.isMappedLanguage(litDocument)
    );
  }

  // This method checks whether it is a mapped language that matched the documents urls.
  private isMappedLanguage(litDocument: LiteratureDocument): boolean {
    /* This will return true always for the sites where language mapping is not set
    // to replicate the previous behaviour.
    */
    if (!this.literatureConfigService.getLiteratureLanguageMapping()?.length) {
      return true;
    }

    // The language mapping can not be validate if downloadUrl or literatureHref is not in the document.
    if (!(litDocument.downloadUrl || litDocument.literatureHref)) {
      return false;
    }

    return this.literatureConfigService
      .getLiteratureLanguageMapping()
      ?.some((lang) =>
        litDocument.dctermsLanguage
          ?.map((langCode) => langCode.toLowerCase())
          .includes(lang.toLowerCase())
      );
  }

  private mergeFundLevelDocuments(
    fundSpecificLiteratureData: object,
    fundId?: FundId
  ): void {
    if (this.fundLevelLitDataMap.has(fundId) && fundId) {
      const litDocuments: LiteratureDocument[] = this.fundLevelLitDataMap.get(
        fundId
      );
      Object.keys(fundSpecificLiteratureData).forEach((shareClassCode) => {
        litDocuments.forEach((litData) => {
          const fundlevelData = {};
          if (
            !fundSpecificLiteratureData[shareClassCode].hasOwnProperty(
              litData.dctermsType
            )
          ) {
            fundlevelData[litData?.dctermsType] = litData;
            fundSpecificLiteratureData[shareClassCode] = {
              ...fundSpecificLiteratureData[shareClassCode],
              ...fundlevelData,
            };
          }
        });
      });
    }
  }

  /**
   * This method will return All share info required for Global Regulatory documents.
   * @param literatureData Regulatory literature document.
   * @param product Product Data.
   * return Object with proiperties shareclassOption, shareClassNames,isins.
   */

  public generateRegulatoryShareClassDetails(
    literatureData: object,
    product: Product
  ): GlobalRegulatoryShareClassDetails {
    const shareClassOption: GlobalRegulatoryShareclassInfo[] = [];
    const shareClassNames: string[] = [];
    const isinValues: ISIN[] = [];

    Object.keys(literatureData).forEach((shareClassCode) => {
      const shareClassDetail: {
        shareclassName: string;
        productDetail: string;
        isin?: ISIN;
      } = this.getShareClassName(product, shareClassCode as ShareClassCode);
      if (shareClassDetail?.shareclassName) {
        shareClassOption.push({
          link: shareClassDetail?.shareclassName,
          id: shareClassCode,
          productDetailLink: shareClassDetail?.productDetail,
        });
        shareClassNames.push(shareClassDetail?.shareclassName);
        if (shareClassDetail?.isin) {
          isinValues.push(shareClassDetail?.isin);
        }
      }
    });
    return {
      shareclassOption: shareClassOption,
      shareClassNames: shareClassNames.join(','),
      isins: isinValues.join(','),
    };
  }

  private getShareClassName(
    product: Product,
    shareclassCode: ShareClassCode
  ): { shareclassName: string; productDetail: string; isin?: ISIN } {
    const shareclassData: ShareClass[] = product.shareClasses.filter(
      (data) => data.shareClassCode === shareclassCode
    );

    if (shareclassData?.length > 0) {
      return {
        shareclassName: shareclassData[0]?.shareClassName || EMDASH,
        productDetail: shareclassData[0]?.link + '#documents',
        isin: shareclassData[0]?.identifiers?.isin,
      };
    }
    // if there is no match on shareclass code then returning object with empty value.
    return {
      shareclassName: '',
      productDetail: '',
      isin: null,
    };
  }

  private getPrimaryShareClass(ppssData: Product): ShareClassCode {
    const primaryShareclassData = ppssData.shareClasses.filter(
      (el) => el.primaryShareClass
    );
    return primaryShareclassData?.length
      ? primaryShareclassData[0].shareClassCode
      : (EMDASH as ShareClassCode);
  }

  /**
   *  segregate the data at multishareclass level & singleshareclass level
   * @param litData Map<DctermType, LiteratureDocument[]>
   * @param ppssData Product
   */
  private setDataOnShareClassLevel(
    litData: Map<DctermType, LiteratureDocument[]>,
    ppssData: Product
  ): void {
    let filteredShareClassCode: string[] = [];
    let sortedFrkShareClassCode: string;

    const pdsShareClassCode: string[] = [];

    ppssData.shareClasses.forEach((pdsShareClass: ShareClass) => {
      pdsShareClassCode.push(pdsShareClass.shareClassCode);
    });

    litData.forEach((value: LiteratureDocument[], key: DctermType) => {
      value.forEach((document: LiteratureDocument) => {
        if (document?.frkShareClassCode) {
          if (document.frkShareClassCode.length > 1) {
            document.frkShareClassCode = this.updateLiteratureShareClassCode(
              document?.frkShareClassCode
            );
            filteredShareClassCode = [];
            document.frkShareClassCode.forEach((shareClassCode: string) => {
              if (
                pdsShareClassCode.includes(shareClassCode) &&
                !filteredShareClassCode.includes(shareClassCode)
              ) {
                filteredShareClassCode.push(shareClassCode);
              }
            });
            sortedFrkShareClassCode = filteredShareClassCode
              .sort((a: string, b: string) => a.localeCompare(b))
              .toString();
            if (sortedFrkShareClassCode.length) {
              if (sortedFrkShareClassCode in this.multiShareClassData) {
                this.multiShareClassData[sortedFrkShareClassCode] = {
                  ...this.multiShareClassData[sortedFrkShareClassCode],
                  [key]: cloneDeep(document),
                };
              } else {
                this.multiShareClassData[sortedFrkShareClassCode] = {
                  [key]: cloneDeep(document),
                };
              }
            }
          } else {
            if (pdsShareClassCode.includes(document.frkShareClassCode[0])) {
              if (document.frkShareClassCode[0] in this.singleShareClassData) {
                this.singleShareClassData[document.frkShareClassCode[0]] = {
                  ...this.singleShareClassData[document.frkShareClassCode[0]],
                  [key]: cloneDeep(document),
                };
              } else {
                this.singleShareClassData[document.frkShareClassCode[0]] = {
                  [key]: cloneDeep(document),
                };
              }
            }
          }
        }
      });
    });
  }

  /**
   * To create multiple singleShareClass Data when single share class
   * is coming up with multiple combination in multiShareClassData
   */
  private splitMultiShareClassDataToSingleShareClassData(): void {
    let shareClassCodes: string[] = [];
    let isDuplicateClassAvailable = false;
    Object.keys(this.multiShareClassData).forEach((key: string) => {
      key.split(',').forEach((classCode) => {
        if (shareClassCodes.indexOf(classCode) === -1) {
          shareClassCodes.push(classCode);
        } else {
          isDuplicateClassAvailable = true;
        }
      });
    });

    if (isDuplicateClassAvailable) {
      shareClassCodes = [];
      Object.keys(this.multiShareClassData).forEach((key) => {
        shareClassCodes = key.split(',');
        shareClassCodes.forEach((classCode) => {
          if (classCode in this.singleShareClassData) {
            this.singleShareClassData[classCode] = {
              ...this.singleShareClassData[classCode],
              ...this.multiShareClassData[key],
            };
          } else {
            this.singleShareClassData[classCode] = {
              ...this.singleShareClassData[classCode],
              ...this.multiShareClassData[key],
            };
          }
        });
      });
      // clearing multiShareClassData
      this.multiShareClassData = {};
    }
  }

  private mergeMultiShareClassDataToSingleShareClassData(
    ppssData: Product,
    isShareclassLevelRegulatory = false
  ): void {
    let flagToAddMultiShareClassRow = true;
    const rowData: object = {};

    Object.keys(this.multiShareClassData).forEach((key) => {
      flagToAddMultiShareClassRow = true;
      let multiShareClassKey: string[] = key.toString().split(',');
      Object.keys(this.singleShareClassData).forEach((key1: string) => {
        if (multiShareClassKey.includes(key1)) {
          rowData[key1] = {
            ...this.singleShareClassData[key1],
            ...this.multiShareClassData[key],
          };
          multiShareClassKey = multiShareClassKey.filter(
            (shareClassCode) => shareClassCode !== key1
          );
          flagToAddMultiShareClassRow = false;
        }
      });

      // To create extra combination when number of single share class data is less than multiShareClass data
      if (multiShareClassKey.length) {
        rowData[multiShareClassKey.toString()] = {
          ...this.multiShareClassData[key],
        };
      }

      // To create a row when there is not exact match for singleShareClass data with multiShareClass data
      if (flagToAddMultiShareClassRow) {
        rowData[key] = { ...this.multiShareClassData[key] };
      }
    });
    this.generateCustomRow(rowData, ppssData, isShareclassLevelRegulatory);
  }

  /**
   *  add numeric, roman and advisor class to literature share class
   */
  private updateLiteratureShareClassCode(
    frkShareClassCode: ShareClassCode[]
  ): ShareClassCode[] {
    const mappedShareClassCode: ShareClassCode[] = [];
    frkShareClassCode?.forEach((el) => {
      if (el.toLowerCase() === ADVISOR) {
        mappedShareClassCode.push(Z_SHARECLASS);
      } else {
        mappedShareClassCode.push(el);
        const tempClassCode: ShareClassCode = this.mapNumericAndRomanCode(el);
        if (tempClassCode && !mappedShareClassCode.includes(tempClassCode)) {
          mappedShareClassCode.push(tempClassCode);
        }
      }
    });
    return mappedShareClassCode;
  }

  private mapNumericAndRomanCode(
    shareClassCode: ShareClassCode
  ): ShareClassCode {
    if (shareClassCode === 'I') {
      return '1' as ShareClassCode;
    }
    if (shareClassCode === 'II') {
      return '2' as ShareClassCode;
    }
    if (shareClassCode === '1') {
      return 'I' as ShareClassCode;
    }
    if (shareClassCode === '2') {
      return 'II' as ShareClassCode;
    }
    if (shareClassCode === 'Primary') {
      return 'P' as ShareClassCode;
    }
    if (shareClassCode === 'Service') {
      return 'S' as ShareClassCode;
    }
    return undefined;
  }

  /**
   * generate row for listing
   */
  private generateCustomRow(
    rowData: object,
    ppssData: Product,
    isShareclassLevelRegulatory = false
  ): void {
    Object.keys(rowData).forEach((key: string) => {
      this.loadCustomDocumentList(
        rowData[key],
        ppssData,
        key.split(','),
        this.setIdentifiers(rowData[key], ppssData),
        this.customListingData,
        isShareclassLevelRegulatory
      );
    });
  }

  private setIdentifiers(litData, ppssData): KeyIdentifiers {
    let identifiers: KeyIdentifiers = this.initializeIdentifier();
    for (const value in litData) {
      if (litData.hasOwnProperty(value)) {
        this.checkShareClassAvailablity(litData[value], ppssData);
        if (litData[value].shareclass) {
          identifiers = this.getKeyIdentifiers(
            [litData[value].shareclass],
            identifiers
          );
        } else {
          identifiers = this.getKeyIdentifiers(
            ppssData.shareClasses,
            identifiers
          );
        }
      }
    }
    return identifiers;
  }

  private initializeIdentifier(): KeyIdentifiers {
    return {
      isin: [],
      bloomberg: [],
      cusip: [],
      reuterSystem: [],
      sedol: [],
      taid: [],
      shareClassFeeStructure: [],
      vln: [],
      wkn: [],
      mexid: [],
      rfi: [],
      apir: [],
      arsn: [],
      adm: [],
      dsc: [],
      ticker: [],
      iress: [],
      iressinavcode: [],
      anbimacode: [],
      intradaynav: [],
      fundbmticker: [],
      distributionIndicator: [],
      dividendFrequency: [],
      shareClassCurrency: [],
    };
  }

  private getKeyIdentifiers(
    shareClasses: ShareClass[],
    identifier // TODO need type
  ): KeyIdentifiers {
    for (const shareClass of shareClasses) {
      identifier.isin.push(shareClass.identifiers.isin);
      identifier.bloomberg.push(shareClass.identifiers.bloomberg);
      identifier.cusip.push(shareClass.identifiers.cusip);
      identifier.reuterSystem.push(shareClass.identifiers.reuterSystem);
      identifier.sedol.push(shareClass.identifiers.sedol);
      identifier.taid.push(shareClass.identifiers.taid);
      identifier.shareClassFeeStructure.push(shareClass.shareClassFeeStructure);
      identifier.shareClassCurrency.push(shareClass.shareClassCurrency);
      identifier.distributionIndicator.push(shareClass.distributionIndicator);
      identifier.dividendFrequency.push(shareClass.dividendFrequency);
      identifier.wkn.push(shareClass.identifiers.wkn);
      identifier.vln.push(shareClass.identifiers.vln);
      identifier.mexid.push(shareClass.identifiers.mexId);
      identifier.rfi.push(shareClass.identifiers.rfi);
      identifier.apir.push(shareClass.identifiers.apir);
      identifier.arsn.push(shareClass.identifiers.arsn);
      identifier.adm.push(shareClass.identifiers.adm);
      identifier.dsc.push(shareClass.identifiers.dsc);
      identifier.ticker.push(shareClass.identifiers.ticker);
      identifier.iress.push(shareClass.identifiers.iress);
      identifier.iressinavcode.push(shareClass.identifiers.iressinavCode);
      identifier.anbimacode.push(shareClass.identifiers.anbimaCode);
      identifier.intradaynav.push(shareClass.identifiers.intradayNav);
      identifier.fundbmticker.push(shareClass.identifiers.fundbmTicker);
    }
    return identifier;
  }

  // TODO: can this be deleted now?
  private isIdentifierValid(key: string): boolean {
    if (key && key.trim() !== '' && key !== '-') {
      return true;
    }
    return false;
  }

  public ngOnDestroy(): void {
    this.onDestroyUnsubscribe();
  }
}
